import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
    Dumbbell,
    Trophy,
    Clock,
    Target,
    Weight,
    RotateCcw,
    Send,
    Calendar,
    Medal,
    User,
    History,
    ChevronRight,
    ArrowLeft
} from 'lucide-react';
import { collection, query, where, getDocs, addDoc, orderBy, limit, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useBusiness } from '../contexts/BusinessContext';
import { useAuth } from '../contexts/AuthContext';
import { WOD, WODResult, ScoreType, formatScore, getScoreTypeName } from '../types/wod';
import { toast } from 'react-hot-toast';

const WODPage: React.FC = () => {
    const { businessId } = useBusiness();
    const { currentUser } = useAuth();
    const [todayWod, setTodayWod] = useState<WOD | null>(null);
    const [results, setResults] = useState<WODResult[]>([]);
    const [myResult, setMyResult] = useState<WODResult | null>(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    // Tab and History state
    const [activeTab, setActiveTab] = useState<'today' | 'history'>('today');
    const [pastWods, setPastWods] = useState<WOD[]>([]);
    const [selectedPastWod, setSelectedPastWod] = useState<WOD | null>(null);
    const [loadingHistory, setLoadingHistory] = useState(false);

    // Form state for submitting result
    const [scoreInput, setScoreInput] = useState('');
    const [notesInput, setNotesInput] = useState('');
    const [timeMinutes, setTimeMinutes] = useState('');
    const [timeSeconds, setTimeSeconds] = useState('');

    useEffect(() => {
        if (businessId && currentUser) {
            loadTodayWod();
        }
    }, [businessId, currentUser]);

    useEffect(() => {
        if (activeTab === 'history' && businessId && pastWods.length === 0) {
            loadPastWods();
        }
    }, [activeTab, businessId]);

    const loadPastWods = async () => {
        if (!businessId) return;
        setLoadingHistory(true);
        try {
            const now = new Date();
            const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

            const wodsQuery = query(
                collection(db, 'wods'),
                where('businessId', '==', businessId),
                where('date', '<', today),
                where('isActive', '==', true),
                orderBy('date', 'desc'),
                limit(20)
            );
            const snapshot = await getDocs(wodsQuery);
            const wods = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as WOD[];
            setPastWods(wods);
        } catch (error) {
            console.error('Error loading past WODs:', error);
            toast.error('Error al cargar el historial');
        } finally {
            setLoadingHistory(false);
        }
    };

    const viewPastWod = async (wod: WOD) => {
        setSelectedPastWod(wod);
        await loadResults(wod.id, wod.scoreType);
    };

    const loadTodayWod = async () => {
        if (!businessId || !currentUser) return;
        setLoading(true);

        try {
            // Use local date instead of UTC
            const now = new Date();
            const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

            console.log('🔍 WOD Query Debug:', {
                businessId,
                queryDate: today,
                localDate: now.toLocaleDateString('es-ES'),
                userId: currentUser.id
            });

            // Get today's WOD
            const wodQuery = query(
                collection(db, 'wods'),
                where('businessId', '==', businessId),
                where('date', '==', today),
                where('isActive', '==', true),
                limit(1)
            );
            const wodSnapshot = await getDocs(wodQuery);
            console.log('🔍 WOD Results:', {
                found: !wodSnapshot.empty,
                count: wodSnapshot.size
            });

            if (!wodSnapshot.empty) {
                const wodData = {
                    id: wodSnapshot.docs[0].id,
                    ...wodSnapshot.docs[0].data()
                } as WOD;
                setTodayWod(wodData);
                await loadResults(wodData.id, wodData.scoreType);
            } else {
                setTodayWod(null);
            }
        } catch (error) {
            console.error('Error loading WOD:', error);
            toast.error('Error al cargar el WOD');
        } finally {
            setLoading(false);
        }
    };

    const loadResults = async (wodId: string, scoreType: ScoreType) => {
        try {
            console.log('📊 Loading results for WOD:', { wodId, businessId, scoreType });
            const resultsQuery = query(
                collection(db, 'wodResults'),
                where('wodId', '==', wodId),
                where('businessId', '==', businessId),
                orderBy('score', scoreType === 'time' ? 'asc' : 'desc')
            );
            const resultsSnapshot = await getDocs(resultsQuery);
            console.log('📊 Results found:', resultsSnapshot.size);
            const resultsData = resultsSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as WODResult[];

            setResults(resultsData);

            // Find my result
            if (currentUser) {
                const mine = resultsData.find(r => r.userId === currentUser.id);
                setMyResult(mine || null);
                if (mine) {
                    setScoreInput(mine.scoreDisplay);
                    setNotesInput(mine.notes || '');
                }
            }
        } catch (error) {
            console.error('Error loading results:', error);
        }
    };

    const handleSubmitResult = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!todayWod || !currentUser || !businessId) return;

        // Parse score based on type
        let numericScore: number;
        let displayScore: string;

        if (todayWod.scoreType === 'time') {
            // Use timeMinutes and timeSeconds
            const mins = parseInt(timeMinutes, 10) || 0;
            const secs = parseInt(timeSeconds, 10) || 0;
            numericScore = mins * 60 + secs;
            displayScore = `${mins}:${secs.toString().padStart(2, '0')}`;
        } else {
            numericScore = parseInt(scoreInput, 10) || 0;
            displayScore = formatScore(numericScore, todayWod.scoreType);
        }

        if (numericScore <= 0) {
            toast.error('Por favor, introduce un resultado válido');
            return;
        }

        setSubmitting(true);
        try {
            // Check if already submitted
            if (myResult) {
                toast.error('Ya has registrado tu resultado para este WOD');
                return;
            }

            await addDoc(collection(db, 'wodResults'), {
                wodId: todayWod.id,
                businessId,
                userId: currentUser.id,
                userName: `${currentUser.firstName} ${currentUser.lastName}`,
                userPhotoUrl: currentUser.profilePhotoUrl || null,
                score: numericScore,
                scoreDisplay: displayScore,
                notes: notesInput.trim() || null,
                createdAt: serverTimestamp()
            });

            toast.success('¡Resultado registrado!');
            await loadResults(todayWod.id, todayWod.scoreType);
        } catch (error) {
            console.error('Error submitting result:', error);
            toast.error('Error al registrar el resultado');
        } finally {
            setSubmitting(false);
        }
    };

    const getScoreIcon = (type: ScoreType) => {
        switch (type) {
            case 'time': return <Clock className="w-5 h-5" />;
            case 'reps': return <Target className="w-5 h-5" />;
            case 'weight': return <Weight className="w-5 h-5" />;
            case 'rounds': return <RotateCcw className="w-5 h-5" />;
        }
    };

    const getPlaceholder = (type: ScoreType) => {
        switch (type) {
            case 'time': return 'Ej: 5:23';
            case 'reps': return 'Ej: 150';
            case 'weight': return 'Ej: 100';
            case 'rounds': return 'Ej: 8';
        }
    };

    const getMedalColor = (position: number) => {
        switch (position) {
            case 1: return 'text-yellow-400';
            case 2: return 'text-gray-300';
            case 3: return 'text-amber-600';
            default: return 'text-gray-500';
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
            </div>
        );
    }

    return (
        <>
            <Helmet>
                <title>WOD del Día</title>
            </Helmet>

            <div className="space-y-6 pb-24">
                {/* Header */}
                <div className="text-center">
                    <h1 className="text-2xl font-bold text-white flex items-center justify-center gap-3">
                        <Dumbbell className="w-7 h-7 text-amber-500" />
                        WOD
                    </h1>
                </div>

                {/* Tabs */}
                <div className="flex bg-gray-900 rounded-xl p-1 gap-1">
                    <button
                        onClick={() => { setActiveTab('today'); setSelectedPastWod(null); }}
                        className={`flex-1 py-2.5 px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-2
                            ${activeTab === 'today'
                                ? 'bg-amber-500 text-black'
                                : 'text-gray-400 hover:text-white'}`}
                    >
                        <Dumbbell className="w-4 h-4" />
                        Hoy
                    </button>
                    <button
                        onClick={() => setActiveTab('history')}
                        className={`flex-1 py-2.5 px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-2
                            ${activeTab === 'history'
                                ? 'bg-amber-500 text-black'
                                : 'text-gray-400 hover:text-white'}`}
                    >
                        <History className="w-4 h-4" />
                        Historial
                    </button>
                </div>

                {/* Today Tab Content */}
                {activeTab === 'today' && (
                    <>
                        <p className="text-center text-gray-400">
                            {new Date().toLocaleDateString('es-ES', {
                                weekday: 'long',
                                day: 'numeric',
                                month: 'long'
                            })}
                        </p>

                        {!todayWod ? (
                            <div className="text-center py-16 bg-gray-900 rounded-2xl border border-gray-800">
                                <Calendar className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                                <h3 className="text-xl font-semibold text-white mb-2">No hay WOD para hoy</h3>
                                <p className="text-gray-400">
                                    El entrenador aún no ha publicado el entrenamiento de hoy
                                </p>
                            </div>
                        ) : (
                            <>
                                {/* WOD Card */}
                                <div className="bg-gradient-to-br from-amber-900/30 to-orange-900/20 rounded-2xl p-6 border border-amber-700/50">
                                    <div className="flex items-center justify-between mb-4">
                                        <h2 className="text-2xl font-bold text-white">{todayWod.title}</h2>
                                        <span className="flex items-center gap-2 px-3 py-1 bg-amber-500/20 text-amber-400 rounded-full text-sm font-medium">
                                            {getScoreIcon(todayWod.scoreType)}
                                            {getScoreTypeName(todayWod.scoreType).split(' ')[0]}
                                        </span>
                                    </div>
                                    <p className="text-gray-200 whitespace-pre-line text-lg leading-relaxed">
                                        {todayWod.description}
                                    </p>
                                </div>

                                {/* Submit Result Form */}
                                {!myResult ? (
                                    <form onSubmit={handleSubmitResult} className="bg-gray-900 rounded-2xl p-5 border border-gray-800">
                                        <h3 className="text-lg font-semibold text-white mb-4">Registra tu Resultado</h3>
                                        <div className="space-y-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-300 mb-1">
                                                    {todayWod.scoreType === 'time' ? 'Tu Tiempo' :
                                                        todayWod.scoreType === 'reps' ? 'Repeticiones Totales' :
                                                            todayWod.scoreType === 'weight' ? 'Peso (kg)' : 'Rondas Completadas'}
                                                </label>
                                                {todayWod.scoreType === 'time' ? (
                                                    <div className="flex items-center gap-2">
                                                        <div className="flex-1">
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                max="59"
                                                                value={timeMinutes}
                                                                onChange={(e) => setTimeMinutes(e.target.value.slice(0, 2))}
                                                                placeholder="00"
                                                                className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white text-2xl text-center focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                                            />
                                                            <span className="block text-center text-xs text-gray-500 mt-1">minutos</span>
                                                        </div>
                                                        <span className="text-3xl text-amber-500 font-bold">:</span>
                                                        <div className="flex-1">
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                max="59"
                                                                value={timeSeconds}
                                                                onChange={(e) => setTimeSeconds(e.target.value.slice(0, 2))}
                                                                placeholder="00"
                                                                className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white text-2xl text-center focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                                            />
                                                            <span className="block text-center text-xs text-gray-500 mt-1">segundos</span>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        value={scoreInput}
                                                        onChange={(e) => setScoreInput(e.target.value)}
                                                        placeholder={getPlaceholder(todayWod.scoreType)}
                                                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white text-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                                        required
                                                    />
                                                )}
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-300 mb-1">
                                                    Notas (opcional)
                                                </label>
                                                <input
                                                    type="text"
                                                    value={notesInput}
                                                    onChange={(e) => setNotesInput(e.target.value)}
                                                    placeholder="Ej: RX, Scaled, con chaleco..."
                                                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                                />
                                            </div>
                                            <button
                                                type="submit"
                                                disabled={submitting}
                                                className="w-full py-3 bg-amber-600 hover:bg-amber-700 disabled:bg-amber-800 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
                                            >
                                                {submitting ? (
                                                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                                                ) : (
                                                    <>
                                                        <Send className="w-5 h-5" />
                                                        Registrar Resultado
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </form>
                                ) : (
                                    <div className="bg-green-900/20 border border-green-700/50 rounded-2xl p-5">
                                        <div className="flex items-center gap-3 mb-2">
                                            <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center">
                                                <Trophy className="w-5 h-5 text-white" />
                                            </div>
                                            <div>
                                                <p className="text-green-400 font-medium">¡Resultado Registrado!</p>
                                                <p className="text-white text-lg font-bold">{myResult.scoreDisplay}</p>
                                            </div>
                                        </div>
                                        {myResult.notes && (
                                            <p className="text-gray-400 text-sm mt-2">Notas: {myResult.notes}</p>
                                        )}
                                    </div>
                                )}

                                {/* Leaderboard */}
                                <div className="bg-gray-900 rounded-2xl border border-gray-800 overflow-hidden">
                                    <div className="p-4 border-b border-gray-800 flex items-center gap-2">
                                        <Trophy className="w-5 h-5 text-amber-500" />
                                        <h3 className="text-lg font-semibold text-white">Pizarra de Resultados</h3>
                                        <span className="ml-auto text-sm text-gray-500">{results.length} participantes</span>
                                    </div>

                                    {results.length === 0 ? (
                                        <div className="p-8 text-center">
                                            <p className="text-gray-400">Sé el primero en registrar tu resultado</p>
                                        </div>
                                    ) : (
                                        <div className="divide-y divide-gray-800">
                                            {results.map((result, index) => (
                                                <div
                                                    key={result.id}
                                                    className={`p-4 flex items-center gap-4 ${result.userId === currentUser?.id ? 'bg-amber-900/10' : ''}`}
                                                >
                                                    <div className={`w-8 h-8 flex items-center justify-center font-bold ${getMedalColor(index + 1)}`}>
                                                        {index < 3 ? (
                                                            <Medal className="w-6 h-6" />
                                                        ) : (
                                                            <span>{index + 1}</span>
                                                        )}
                                                    </div>
                                                    <div className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center overflow-hidden">
                                                        {result.userPhotoUrl ? (
                                                            <img src={result.userPhotoUrl} alt="" className="w-full h-full object-cover" />
                                                        ) : (
                                                            <User className="w-5 h-5 text-gray-400" />
                                                        )}
                                                    </div>
                                                    <div className="flex-1">
                                                        <p className="text-white font-medium">{result.userName}</p>
                                                        {result.notes && (
                                                            <p className="text-xs text-gray-500">{result.notes}</p>
                                                        )}
                                                    </div>
                                                    <div className="text-right">
                                                        <p className="text-lg font-bold text-amber-400">{result.scoreDisplay}</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </>
                )}

                {/* History Tab Content */}
                {activeTab === 'history' && (
                    <>
                        {selectedPastWod ? (
                            /* Viewing a specific past WOD */
                            <>
                                <button
                                    onClick={() => setSelectedPastWod(null)}
                                    className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
                                >
                                    <ArrowLeft className="w-4 h-4" />
                                    Volver al historial
                                </button>

                                <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 rounded-2xl p-6 border border-gray-700">
                                    <div className="flex items-center justify-between mb-2">
                                        <h2 className="text-xl font-bold text-white">{selectedPastWod.title}</h2>
                                        <span className="flex items-center gap-2 px-3 py-1 bg-gray-700/50 text-gray-300 rounded-full text-sm font-medium">
                                            {getScoreIcon(selectedPastWod.scoreType)}
                                            {getScoreTypeName(selectedPastWod.scoreType).split(' ')[0]}
                                        </span>
                                    </div>
                                    <p className="text-sm text-gray-500 mb-3">
                                        {new Date(selectedPastWod.date + 'T00:00:00').toLocaleDateString('es-ES', {
                                            weekday: 'long',
                                            day: 'numeric',
                                            month: 'long'
                                        })}
                                    </p>
                                    <p className="text-gray-300 whitespace-pre-line">{selectedPastWod.description}</p>
                                </div>

                                {/* Leaderboard for past WOD */}
                                <div className="bg-gray-900 rounded-2xl border border-gray-800 overflow-hidden">
                                    <div className="p-4 border-b border-gray-800 flex items-center gap-2">
                                        <Trophy className="w-5 h-5 text-amber-500" />
                                        <h3 className="text-lg font-semibold text-white">Ranking Final</h3>
                                        <span className="ml-auto text-sm text-gray-500">{results.length} participantes</span>
                                    </div>

                                    {results.length === 0 ? (
                                        <div className="p-8 text-center">
                                            <p className="text-gray-400">No hubo participantes en este WOD</p>
                                        </div>
                                    ) : (
                                        <div className="divide-y divide-gray-800">
                                            {results.map((result, index) => (
                                                <div
                                                    key={result.id}
                                                    className={`p-4 flex items-center gap-4 ${result.userId === currentUser?.id ? 'bg-amber-900/10' : ''}`}
                                                >
                                                    <div className={`w-8 h-8 flex items-center justify-center font-bold ${getMedalColor(index + 1)}`}>
                                                        {index < 3 ? (
                                                            <Medal className="w-6 h-6" />
                                                        ) : (
                                                            <span>{index + 1}</span>
                                                        )}
                                                    </div>
                                                    <div className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center overflow-hidden">
                                                        {result.userPhotoUrl ? (
                                                            <img src={result.userPhotoUrl} alt="" className="w-full h-full object-cover" />
                                                        ) : (
                                                            <User className="w-5 h-5 text-gray-400" />
                                                        )}
                                                    </div>
                                                    <div className="flex-1">
                                                        <p className="text-white font-medium">{result.userName}</p>
                                                        {result.notes && (
                                                            <p className="text-xs text-gray-500">{result.notes}</p>
                                                        )}
                                                    </div>
                                                    <div className="text-right">
                                                        <p className="text-lg font-bold text-amber-400">{result.scoreDisplay}</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </>
                        ) : (
                            /* List of past WODs */
                            <>
                                {loadingHistory ? (
                                    <div className="flex items-center justify-center h-32">
                                        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500"></div>
                                    </div>
                                ) : pastWods.length === 0 ? (
                                    <div className="text-center py-16 bg-gray-900 rounded-2xl border border-gray-800">
                                        <Calendar className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                                        <h3 className="text-xl font-semibold text-white mb-2">Sin historial</h3>
                                        <p className="text-gray-400">Aún no hay WODs pasados para mostrar</p>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {pastWods.map(wod => (
                                            <button
                                                key={wod.id}
                                                onClick={() => viewPastWod(wod)}
                                                className="w-full bg-gray-900 rounded-xl p-4 border border-gray-800 hover:border-amber-600/50 transition-all text-left group"
                                            >
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <p className="text-white font-semibold group-hover:text-amber-400 transition-colors">
                                                            {wod.title}
                                                        </p>
                                                        <p className="text-sm text-gray-500">
                                                            {new Date(wod.date + 'T00:00:00').toLocaleDateString('es-ES', {
                                                                weekday: 'short',
                                                                day: 'numeric',
                                                                month: 'short'
                                                            })}
                                                        </p>
                                                    </div>
                                                    <ChevronRight className="w-5 h-5 text-gray-500 group-hover:text-amber-400 transition-colors" />
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </>
                )}
            </div>
        </>
    );
};

export default WODPage;
